import { atom as jotaiAtom } from "jotai";
import { atomWithStorage, createJSONStorage } from "jotai/utils";

interface StringStorage {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
}

const fallbackStorage: StringStorage = {
  getItem: () => null,
  setItem: () => undefined,
  removeItem: () => undefined,
};

const isStringStorage = (storage: unknown): storage is StringStorage => {
  if (typeof storage !== "object" || storage === null) {
    return false;
  }

  return (
    "getItem" in storage &&
    typeof storage.getItem === "function" &&
    "setItem" in storage &&
    typeof storage.setItem === "function" &&
    "removeItem" in storage &&
    typeof storage.removeItem === "function"
  );
};

const getStringStorage = (): StringStorage => {
  try {
    const storage = globalThis.localStorage;
    return isStringStorage(storage) ? storage : fallbackStorage;
  } catch {
    return fallbackStorage;
  }
};

export const omniStorage = <T>() => createJSONStorage<T>(getStringStorage);

export const omniAtom = <T>({ prefix, key, value }: { prefix?: string; key: string; value: T }) => {
  const atom = jotaiAtom(value);
  atom.debugLabel = (prefix ? prefix + "/" : "") + key;
  return atom;
};

/**
 * Like {@link omniAtom} but persisted to localStorage, so the value sticks per
 * user/session across page reloads. The prefixed debug label doubles as the
 * (namespaced) storage key.
 */
export const omniAtomWithStorage = <T>({
  prefix,
  key,
  value,
}: {
  prefix?: string;
  key: string;
  value: T;
}) => {
  const label = (prefix ? prefix + "/" : "") + key;
  const atom = atomWithStorage<T>(label, value, omniStorage<T>());
  atom.debugLabel = label;
  return atom;
};
